import { Component, DoCheck, ElementRef, HostBinding, Input, OnDestroy, OnInit, Optional, Self } from '@angular/core';
import { ControlValueAccessor, UntypedFormControl, NgControl } from '@angular/forms';
import { MatFormFieldControl } from '@angular/material/form-field';

import { FocusMonitor } from '@angular/cdk/a11y';
import { coerceBooleanProperty } from '@angular/cdk/coercion';

import { Subject } from 'rxjs';

import { OrganizationService, Organization, OrganizationFilterRequest, SalesOrganization } from '@core/api';
import { SharedModule } from '@shared/modules';

@Component({
  selector: 'net-select-organization',
  templateUrl: './select-organization.component.html',
  styleUrls: ['./select-organization.component.scss'],
  providers: [
    {
      provide: MatFormFieldControl,
      useExisting: SelectOrganizationComponent
    }
  ],
  standalone: true,
  imports: [
    SharedModule
  ]
})
export class SelectOrganizationComponent implements OnInit, OnDestroy, DoCheck, ControlValueAccessor, MatFormFieldControl<Organization | null> {

  static nextId = 0;

  loading = false;
  focused = false;
  errorState = false;
  controlType = 'select-organization';
  describedBy = '';
  stateChanges = new Subject<void>();

  organizations: Organization[] = [];
  selectCtrl = new UntypedFormControl();

  @Input() tabIndex;

  @Input()
  get salesOrganization() { return this._salesOrganization; }
  set salesOrganization(value) {
    this._salesOrganization = value;
    this.fetchList();
  }
  private _salesOrganization: SalesOrganization;

  @Input()
  get placeholder(): string { return this._placeholder; }
  set placeholder(value: string) {
    this._placeholder = value;
    this.stateChanges.next();
  }
  private _placeholder: string;

  @Input()
  get required(): boolean { return this._required; }
  set required(value: boolean) {
    this._required = coerceBooleanProperty(value);
    this.stateChanges.next();
  }
  private _required = false;

  @Input()
  get disabled(): boolean { return this._disabled; }
  set disabled(value: boolean) {
    if (value) {
      this.selectCtrl.disable();
    } else {
      this.selectCtrl.enable();
    }

    this._disabled = value;
    this.stateChanges.next();
  }
  private _disabled = false;

  @Input()
  get value(): Organization | null { return this.selectCtrl.value; }
  set value(value: Organization | null) {
    this.selectCtrl.setValue(value);
    this.onChange(value);
    this.stateChanges.next();
  }

  @HostBinding('id') id = `select-organization-${SelectOrganizationComponent.nextId++}`;
  @HostBinding('attr.aria-describedby') describedByBinding = this.describedBy;

  get shouldLabelFloat() { return this.focused || !this.empty; }

  get empty() { return !this.value; }

  constructor(
    private _focusMonitor: FocusMonitor,
    private _elementRef: ElementRef<HTMLElement>,
    private organizationService: OrganizationService,
    @Optional() @Self() public ngControl: NgControl
  ) {
    // Material form field implementation
    _focusMonitor.monitor(_elementRef, true).subscribe(origin => {
      if (this.disabled) {
        return;
      }

      if (this.focused && !origin) {
        this.onTouched();
      }
      this.focused = !!origin;
      this.stateChanges.next();
    });

    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }
  }

  private fetchList() {
    this.loading = true;
    const request: OrganizationFilterRequest = { filter: {} };

    if (this.salesOrganization) {
      request.filter.salesOrganizationId = this.salesOrganization.salesOrganizationId;
    }

    this.organizationService.search(request).subscribe(response => {
      this.loading = false;
      this.organizations = response.data.results;

      if (this.organizations.length === 1) {
        this.value = this.organizations[0];
      }
    });
  }

  ngOnInit() {
    this.fetchList();
  }

  onSelectionChange() {
    this.onChange(this.value);
    this.stateChanges.next();
  }

  onChange = (_: any) => { };
  onTouched = () => { };
  compareWith = (one: Organization, two: Organization) => one && two && one.organizationId === two.organizationId;
  onContainerClick = () => { };
  registerOnChange = (fn: any) => this.onChange = fn;
  registerOnTouched = (fn: any) => this.onTouched = fn;
  setDisabledState = (isDisabled: boolean) => this.disabled = isDisabled;
  setDescribedByIds = (ids: string[]) => this.describedBy = ids.join(' ');
  writeValue = (value: Organization) => this.value = value;
  ngOnDestroy = () => this.stateChanges.complete();

  ngDoCheck() {
    // Reflect control valid status for mat form field error state
    if (this.ngControl) {
      this.errorState = this.ngControl.invalid && this.ngControl.touched;
      this.stateChanges.next();
    }
  }
}
